import DiscoverService from '@/services/discover';
import { chartColors } from '@/modules/formulary/config/colorPalette';

// Discover store module
//
// Owns ONLY what is specific to the Discover page: the bar-chart configuration
// (metric catalog, selected metrics, medications-to-show limit), the computed
// chartData, and the resolved filter payload that feeds the chart query.
// Shared datasets live in the `reference` module.
//
// Matches the target application's module convention: no `namespaced`, object
// `state`, SCREAMING_SNAKE mutations (with a `*_LOADED` flag for async data),
// `setX` actions that return a Promise, and `x` / `xLoaded` getters.

// Metric catalog. Beyond the display fields, each metric carries two hints the
// chart needs:
//
//   format       d3-format string for the value label drawn above each bar.
//   suffix       Literal appended after the formatted value (d3-format has no
//                syntax for a trailing literal).
//   normalizeMax Fixed ceiling for bar-height normalization. Only set it for
//                metrics with a conceptual maximum (a percentage); metrics
//                without one normalize to their own max in the current dataset.
const AVAILABLE_METRICS = [
  {
    value: 'most_skus',
    label: 'Formulations',
    color: chartColors.primary[0], // #245482 - Dark Blue
    format: ',.0f',
    tooltip: 'The number of formulations identified in the Infusion Pump data',
  },
  {
    value: 'most_wasted',
    label: 'Waste',
    color: chartColors.primary[2], // #24822a - Dark Green
    // Values arrive as 0-100, so a plain number plus a literal '%' is correct
    // here; d3's '%' format type would multiply by 100 again.
    format: '.1~f',
    suffix: '%',
    normalizeMax: 100,
    tooltip:
      'The estimated percentage of total units dispensed that were not infused (equal to Estimated Total Units Wasted / Estimated Total Units Dispensed)',
  },
  {
    value: 'most_utilized',
    label: 'Infusions',
    color: chartColors.primary[1], // #7fbad4 - Medium Blue
    format: ',.0f',
    tooltip:
      'The estimated total number of infusions that were dispensed and run on infusion pumps (also known as utilization)',
  },
  {
    value: 'most_bag_changes',
    label: 'Bags/Patient/Day',
    color: chartColors.primary[3], // #7b8b96 - Medium Gray
    format: ',.1~f',
    tooltip: 'The estimated average infusions per patient per day on pump',
  },
];

export default {
  state: {
    availableMetrics: AVAILABLE_METRICS,
    selectedMetrics: ['most_skus', 'most_wasted'],
    chartLimit: 10,
    chartData: [],
    chartDataLoaded: false,

    // Resolved filter payload used to build chart queries. Mirrors the pieces
    // of the shared filter state + date range + preferences that feed the query.
    filters: {
      dateFilterApplied: false,
      startDate: '',
      endDate: '',
      facility: [],
      patientPopulation: [],
      therapy: [],
      inclusions: [],
      exclusions: [],
      minInfusions: 5,
    },
  },

  mutations: {
    SET_DISCOVER_SELECTED_METRICS(state, selectedMetrics) {
      state.selectedMetrics = selectedMetrics;
    },
    SET_DISCOVER_CHART_LIMIT(state, chartLimit) {
      state.chartLimit = chartLimit;
    },
    SET_DISCOVER_CHART_DATA(state, chartData) {
      state.chartData = chartData;
    },
    SET_DISCOVER_CHART_DATA_LOADED(state, loaded) {
      state.chartDataLoaded = loaded;
    },
    SET_DISCOVER_FILTERS(state, filters) {
      state.filters = { ...state.filters, ...filters };
    },
  },

  actions: {
    // Loads chart data for every selected metric in ONE request.
    //
    // The service sends the whole metric list, the filter payload and the
    // display limit together; the backend aggregates all of them in a single
    // pass and returns rows already pivoted per medication, sorted by the
    // selected metrics in order and trimmed to the limit. Nothing is merged,
    // sorted or sliced here any more — see DISCOVER_DATA_CONTRACTS.md #8.
    setDiscoverChartData({ commit, state }) {
      commit('SET_DISCOVER_CHART_DATA_LOADED', false);
      return (
        DiscoverService.getChartData({
          metrics: state.selectedMetrics,
          filters: state.filters,
          limit: state.chartLimit,
        })
          .then((response) => {
            // Contract #8: [{ name, metrics }], either bare or under `results`.
            const rows = Array.isArray(response) ? response : response.results || [];
            commit(
              'SET_DISCOVER_CHART_DATA',
              rows.map((row) => ({ name: row.name, metrics: row.metrics || {} }))
            );
          })
          // Swallowed, not re-thrown: every caller dispatches this without a
          // `.catch` (see FormularyDiscover's reloadChart), and the empty chart
          // plus `chartDataLoaded` is the whole failure UI.
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Failed to load chart data:', error);
            commit('SET_DISCOVER_CHART_DATA', []);
          })
          .finally(() => {
            commit('SET_DISCOVER_CHART_DATA_LOADED', true);
          })
      );
    },

    setDiscoverSelectedMetrics({ commit, dispatch }, selectedMetrics) {
      commit('SET_DISCOVER_SELECTED_METRICS', selectedMetrics);
      return dispatch('setDiscoverChartData');
    },

    setDiscoverChartLimit({ commit, dispatch }, chartLimit) {
      commit('SET_DISCOVER_CHART_LIMIT', chartLimit);
      return dispatch('setDiscoverChartData');
    },

    setDiscoverFilters({ commit, dispatch }, filters) {
      commit('SET_DISCOVER_FILTERS', filters);
      return dispatch('setDiscoverChartData');
    },
  },

  getters: {
    discoverAvailableMetrics: (state) => state.availableMetrics,
    discoverSelectedMetrics: (state) => state.selectedMetrics,
    discoverChartLimit: (state) => state.chartLimit,
    discoverChartData: (state) => state.chartData,
    discoverChartDataLoaded: (state) => state.chartDataLoaded,

    // Metrics sorted so selected ones come first, in selection order.
    discoverSortedMetrics: (state) => {
      const selected = state.selectedMetrics
        .map((value) => state.availableMetrics.find((m) => m.value === value))
        .filter(Boolean);
      const unselected = state.availableMetrics.filter((m) => !state.selectedMetrics.includes(m.value));
      return [...selected, ...unselected];
    },

    discoverFilters: (state) => state.filters,
  },
};
